import { X } from 'lucide-react'

import { displayName } from '@/shared/opensearch/utils/prefixer'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { useAppliedFilters } from '../../hooks/use-applied-filters'

import type { SearchFilter } from '../../types'

/**
 * Multi-value-aware filter badges. Sidebar `+/−` clicks aggregate into
 * `{f, o: 'in'|'not_in', v: [...]}` entries — one badge per (field, polarity)
 * shows the joined values. Clicking X removes the whole entry.
 */
export const SearchFilters = () => {
	const { filters, removeFilter } = useAppliedFilters()

	if (filters.length === 0) return null

	return (
		<div className="flex min-h-4 flex-row flex-wrap items-center gap-2">
			<p className="text-muted-foreground text-sm">Filters: </p>

			{filters.map((filter, index) => (
				<Badge
					key={`${filter.f}:${filter.o}:${index}`}
					className="flex flex-row items-center gap-2"
				>
					{renderFilter(filter)}
					<Button
						variant="ghost"
						size="icon"
						className="size-4"
						onClick={() => removeFilter(index)}
					>
						<X className="size-4" />
					</Button>
				</Badge>
			))}
		</div>
	)
}

function renderFilter(filter: SearchFilter): React.ReactNode {
	const field = displayName(filter.f)
	const value = Array.isArray(filter.v) ? filter.v.join(', ') : filter.v
	const isNegated = filter.o === 'neq' || filter.o === 'not_in'

	if (isNegated) {
		return (
			<>
				<span className="dark:text-red-500">NOT</span>
				{field}: {value}
			</>
		)
	}

	return `${field}: ${value}`
}
